import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Wrench,
  ArrowRight,
  ShieldCheck,
  Clock4,
  Sparkles,
  MapPin,
  MessageCircle,
} from 'lucide-react';

/**
 * Home-page showcase for the Car Service vertical.
 * Sits as a dedicated section on /IN home so paid traffic that lands on
 * the homepage (vs the dedicated /carservice LP) still discovers the
 * service. Clean two-column layout — left: copy + trust pills + dual CTAs,
 * right: feature grid with the four core promises.
 */
const CarServiceShowcase = () => {
  const navigate = useNavigate();
  const whatsappHref =
    'https://wa.me/919905129146?text=' +
    encodeURIComponent(
      'Hi, I am interested in car service. Let me know the pricing!',
    );

  const promises = [
    {
      icon: MapPin,
      title: 'Doorstep Service',
      copy: 'Free pickup & drop across Bengaluru. We come to you.',
    },
    {
      icon: Clock4,
      title: 'Service in 1 Hr',
      copy: 'Most general services finish in under 60 minutes.',
    },
    {
      icon: Sparkles,
      title: 'OEM Parts Only',
      copy: 'Genuine engine oil, filters & fluids — never refurbished.',
    },
    {
      icon: ShieldCheck,
      title: '1,000 Km Warranty',
      copy: '30-day or 1,000 km — whichever comes first. No fine print.',
    },
  ];

  return (
    <section
      id="car-service-showcase"
      className="relative py-20 lg:py-28 bg-gradient-to-b from-white via-blue-50/40 to-white overflow-hidden"
      data-testid="home-carservice-showcase"
    >
      {/* Subtle blue blobs */}
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-blue-200/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-blue-200/30 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left — Copy + CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 mb-5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700">
                New · Service Vertical
              </span>
            </div>

            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              Premium car service,{' '}
              <span className="text-blue-600">at your doorstep.</span>
            </h2>

            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Skip the workshop queue. Get a transparent quote, book in 30
              seconds, and a Wisedrive-certified mechanic services your car at
              your doorstep with OEM parts and a written warranty.
            </p>

            {/* Pricing chip */}
            <div className="mt-6 inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-white border-2 border-slate-900 shadow-sm">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Starting at
              </div>
              <div className="text-2xl font-black text-slate-900 font-mono">
                ₹3,999
              </div>
              <div className="text-xs font-semibold text-slate-500 leading-tight">
                Hatchback · Petrol<br />
                <span className="text-slate-400">all-inclusive</span>
              </div>
            </div>

            {/* Dual CTAs */}
            <div className="mt-7 flex flex-col sm:flex-row gap-3 max-w-md">
              <button
                onClick={() => navigate('/carservice')}
                data-testid="home-carservice-cta-book"
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-extrabold tracking-tight transition-all shadow-[0_8px_24px_-6px_rgba(37,99,235,0.5)] hover:shadow-[0_12px_28px_-6px_rgba(37,99,235,0.6)] hover:-translate-y-[2px]"
              >
                <Wrench className="h-5 w-5" />
                Book Service
                <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-carservice-cta-whatsapp"
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-white border-2 border-green-600 text-green-700 hover:bg-green-50 font-bold transition-all"
              >
                <MessageCircle className="h-5 w-5" />
                WhatsApp
              </a>
            </div>

            <p className="mt-3 text-xs text-slate-500">
              <strong className="text-slate-700">Save ₹500 extra</strong> when
              you book over WhatsApp · Reply within 2 mins
            </p>
          </motion.div>

          {/* Right — Promise grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden shadow-[0_12px_40px_-12px_rgba(15,23,42,0.18)]"
          >
            {promises.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="bg-white p-6 lg:p-7 hover:bg-blue-50/40 transition-colors"
                  data-testid={`home-carservice-promise-${p.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 inline-flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-blue-600" strokeWidth={2.2} />
                  </div>
                  <div className="text-base font-bold text-slate-900 mb-1.5 leading-tight">
                    {p.title}
                  </div>
                  <div className="text-sm text-slate-600 leading-relaxed">
                    {p.copy}
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CarServiceShowcase;
